/** Stage E — Global Command Palette (⌘K / Ctrl+K)
 *
 * A single unified surface that combines:
 *   - Navigation
 *   - Universal search (symbols / analyses / opportunities / positions / notes / portfolios)
 *   - Commands (Analyze / Refresh / Prepare Trade / Run Scanner / …)
 *   - Recent items
 *   - AI Actions
 *
 * Behaviour:
 *   - Global hotkey ⌘K / Ctrl+K opens
 *   - `/` focuses the current page's visible search field (if one exists) OR opens palette
 *   - ESC closes
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LayoutDashboard, TrendingUp, BarChart2, Wallet, Settings, ClipboardList, Sparkles, RefreshCw, Radar, Search, Compass, PieChart, Book, PlusCircle, StickyNote, Activity } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const NAV_ITEMS = [
  { key: "dashboard", label: "Open Dashboard", href: "/", icon: LayoutDashboard, shortcut: "G D" },
  { key: "opportunities", label: "Open Opportunities", href: "/opportunities", icon: Compass, shortcut: "G O" },
  { key: "history", label: "Open Analysis History", href: "/analysis", icon: Book, shortcut: "G H" },
  { key: "positions", label: "Open Positions", href: "/positions", icon: TrendingUp, shortcut: "G P" },
  { key: "portfolios", label: "Open Portfolios", href: "/portfolios", icon: PieChart, shortcut: "G F" },
  { key: "settings", label: "Open Settings", href: "/settings", icon: Settings, shortcut: "G S" },
];

export default function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const debRef = useRef(null);

  // Global hotkeys ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "/" && !open) {
        const t = e.target;
        const isEditable = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
        if (!isEditable) {
          const searchInput = document.querySelector('[data-testid$="-search-input"], [data-tm-search="true"], input[placeholder*="Search" i]');
          if (searchInput) {
            e.preventDefault();
            searchInput.focus();
          } else {
            e.preventDefault();
            setOpen(true);
          }
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Load recent items when opening
  useEffect(() => {
    if (!open) return;
    api.get("/search/recent").then(r => setRecent(r.data?.items || [])).catch(() => {});
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!query || query.trim().length === 0) { setResults([]); return; }
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => {
      setLoading(true);
      api.get("/search", { params: { q: query, limit: 20 } })
        .then(r => setResults(r.data?.results || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 150);
  }, [query, open]);

  const close = useCallback(() => { setOpen(false); setQuery(""); setResults([]); }, []);

  const openDrawer = (symbol) => {
    window.dispatchEvent(new CustomEvent("tm:open-drawer", { detail: { symbol } }));
  };

  const runAnalyze = async () => {
    const sym = query.trim().toUpperCase();
    if (!sym) { toast.error("Type a symbol first (e.g. NVDA)"); return; }
    try {
      await api.post("/analysis/refresh", { symbol: sym });
      toast.success(`Refresh queued for ${sym}`);
      openDrawer(sym);
      close();
    } catch (e) { toast.error(e?.response?.data?.detail || "Analyze failed"); }
  };

  const runRefreshCurrent = async () => {
    const sym = query.trim().toUpperCase();
    if (sym) return runAnalyze();
    toast.info("Type a symbol to refresh (e.g. NVDA)");
  };

  const runRefreshRegime = async () => {
    try {
      const r = await api.get("/regime/current");
      window.dispatchEvent(new Event("tm:regime-refresh"));
      toast.success(`Regime refreshed · ${r.data?.primary_regime?.replace("_"," ") || "OK"}`);
      close();
    } catch (e) { toast.error("Regime refresh failed"); }
  };

  const goto = (href) => { nav(href); close(); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} commandProps={{ shouldFilter: false }}>
      <DialogTitle className="sr-only">Command Palette</DialogTitle>
      <DialogDescription className="sr-only">Search symbols, run commands, and navigate the workspace</DialogDescription>
      <CommandInput
        placeholder="Search symbols, opportunities, positions, notes… or type a command"
        value={query}
        onValueChange={setQuery}
        data-testid="palette-input"
      />
      <CommandList data-testid="palette-list">
        <CommandEmpty>{loading ? "Searching…" : "No results found."}</CommandEmpty>

        {/* AI Actions — always visible for quick-fire */}
        <CommandGroup heading="AI Actions">
          <CommandItem onSelect={runAnalyze} data-testid="palette-cmd-analyze">
            <Sparkles/><span>Analyze {query.trim().toUpperCase() || "…"}</span>
            <span className="ml-auto mono text-[10px] text-muted-foreground">enter to run</span>
          </CommandItem>
          <CommandItem onSelect={runRefreshCurrent} data-testid="palette-cmd-refresh">
            <RefreshCw/><span>Refresh Analysis</span>
          </CommandItem>
          <CommandItem onSelect={() => goto("/opportunities")} data-testid="palette-cmd-brief">
            <Radar/><span>Generate Morning Brief</span>
          </CommandItem>
          <CommandItem onSelect={runRefreshRegime} data-testid="palette-cmd-refresh-regime">
            <Activity/><span>Refresh Market Regime</span>
          </CommandItem>
          <CommandItem onSelect={() => goto("/positions")} data-testid="palette-cmd-review-positions">
            <ClipboardList/><span>Review Open Positions</span>
          </CommandItem>
        </CommandGroup>

        {/* Search results */}
        {results.length > 0 && (
          <>
            <CommandSeparator/>
            <CommandGroup heading={`Results (${results.length})`}>
              {results.map(r => (
                <CommandItem
                  key={`${r.kind}:${r.id}`}
                  onSelect={() => {
                    if (r.kind === "opportunity" || r.kind === "symbol") {
                      openDrawer(r.symbol);
                    } else if (r.href) {
                      nav(r.href);
                    }
                    close();
                  }}
                  data-testid={`palette-result-${r.kind}-${r.symbol || r.id}`}
                >
                  <Search/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{r.title}</div>
                    <div className="mono text-[10px] text-muted-foreground truncate">{r.subtitle}</div>
                  </div>
                  {r.badge && <span className="chip !text-[9px] border-current text-muted-foreground ml-2">{r.badge}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Recent items */}
        {recent.length > 0 && !query && (
          <>
            <CommandSeparator/>
            <CommandGroup heading="Recent">
              {recent.map(r => (
                <CommandItem
                  key={`recent:${r.symbol || r.id}`}
                  onSelect={() => { openDrawer(r.symbol); close(); }}
                  data-testid={`palette-recent-${r.symbol}`}
                >
                  <TrendingUp/><span>{r.title}</span>
                  {r.subtitle && <span className="ml-auto mono text-[10px] text-muted-foreground">{r.subtitle}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator/>
        <CommandGroup heading="Navigate">
          {NAV_ITEMS.map(n => (
            <CommandItem key={n.key} onSelect={() => goto(n.href)} data-testid={`palette-nav-${n.key}`}>
              <n.icon/><span>{n.label}</span>
              <span className="ml-auto mono text-[10px] text-muted-foreground">{n.shortcut}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator/>
        <CommandGroup heading="Commands">
          <CommandItem onSelect={runAnalyze} data-testid="palette-cmd-analyze-2">
            <PlusCircle/><span>Analyze {query.trim().toUpperCase() || "SYMBOL"}</span>
          </CommandItem>
          <CommandItem onSelect={() => goto("/opportunities")} data-testid="palette-cmd-scanner">
            <Radar/><span>Run Scanner</span>
          </CommandItem>
          <CommandItem onSelect={() => goto("/analysis")} data-testid="palette-cmd-history">
            <Book/><span>Search History</span>
          </CommandItem>
          <CommandItem onSelect={() => goto("/portfolios")} data-testid="palette-cmd-portfolio">
            <Wallet/><span>Open Portfolio Dashboard</span>
          </CommandItem>
          <CommandItem onSelect={() => goto("/settings")} data-testid="palette-cmd-invite">
            <Settings/><span>Invite User / Settings</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
